import {
    DEFAULT_CENSOR_MASK,
    EUROPACE_COMPLETENESS_STATUS,
    EUROPACE_FEASIBILITY_STATUS,
    EUROPACE_INSURED_RISKS,
    OFFER_ACCEPTANCE_STATUSES,
} from 'cpool-constants';
import type { SchemaEuropaceAntrag } from 'cpool-schemas';
import {
    schemaEuropaceAcceptedOffer,
    schemaEuropaceOffer,
    schemaEuropacePartnerId,
    schemaId,
    schemaVorgangsnummer,
} from 'cpool-schemas';
import type { ZodType } from 'zod';
import { z } from 'zod';

import { censorEuropaceAntrag, censorEuropaceAntragApplicant2Only } from '../../../../../utils/censor.utils';
import type { EventSchema } from '../../../../../utils/EventSchema';
import { _Event } from '../../../../_Event';

/**
 * `response.data.data.annahmeJob.antrag` when the KEX offer-acceptance response has that path, for in-place censoring.
 * TS cannot infer `SchemaEuropaceAntrag` from `z.unknown()`; at runtime the nested ant matches the same shape as `acceptedOffer.antrag`.
 */
const getAnnahmeJobAntragFromEuropaceResponse = (response: unknown): SchemaEuropaceAntrag | undefined => {
    if (response == null || typeof response !== 'object' || !('data' in response)) return undefined;
    const d0 = (response as { data: unknown }).data;
    if (d0 == null || typeof d0 !== 'object' || !('data' in d0)) return undefined;
    const d1 = (d0 as { data: unknown }).data;
    if (d1 == null || typeof d1 !== 'object' || !('annahmeJob' in d1)) return undefined;
    const job = (d1 as { annahmeJob: unknown }).annahmeJob;
    if (job == null || typeof job !== 'object') return undefined;
    const { antrag } = job as { antrag?: unknown };
    if (typeof antrag !== 'object' || antrag === null) return undefined;
    return antrag as SchemaEuropaceAntrag;
};

export class LoanRequest_EuropaceOfferAcceptanceResponseSuccessReceived extends _Event {
    constructor(payload: EventSchema<LoanRequest_EuropaceOfferAcceptanceResponseSuccessReceived>, deprecated = false) {
        super(payload, deprecated);
    }

    getSchema() {
        return z.object({
            acceptedOffer: schemaEuropaceAcceptedOffer,
            europaceOffer: schemaEuropaceOffer,
            // this is our internal id
            id: schemaId,
            jobId: z.string().min(1),
            // this is the id we receive from europace
            offerId: z.string().min(1),
            offerRequestId: schemaId,
            partnerId: schemaEuropacePartnerId, // partnerId for the requesting user
            processId: schemaVorgangsnummer,
            response: z.unknown(), // the complete response from europace
        });
    }

    getCensoredSchema(): ZodType {
        return this.getSchema().transform((data) => {
            if (data.acceptedOffer?.antrag) censorEuropaceAntrag(data.acceptedOffer.antrag);
            const annahmeAntrag = getAnnahmeJobAntragFromEuropaceResponse(data.response);
            if (annahmeAntrag) censorEuropaceAntrag(annahmeAntrag);
            return data;
        });
    }

    getCensoredSchemaApplicant2Only(_applicant2Id: string) {
        return this.getSchema().transform((data) => {
            if (data.acceptedOffer?.antrag) censorEuropaceAntragApplicant2Only(data.acceptedOffer.antrag);
            const annahmeAntrag = getAnnahmeJobAntragFromEuropaceResponse(data.response);
            if (annahmeAntrag) censorEuropaceAntragApplicant2Only(annahmeAntrag);
            return data;
        });
    }

    static example() {
        return {
            acceptedOffer: {
                antrag: {
                    antragsnummer: 'ABC123/1/1',
                    dokumente: [
                        { url: 'https://example.com/document1.pdf' },
                        { url: 'https://example.com/document2.pdf]' },
                    ],
                    gesamtkonditionen: {
                        effektivzins: 2.99,
                        gesamtkreditbetrag: 10762.19,
                        laufzeitInMonaten: 60,
                        nettokreditbetrag: 10000,
                        rateMonatlich: 179.47,
                        sollzins: 2.95,
                    },
                    identifikationAntragsteller1: {
                        antragstellername: 'John Doe',
                        qesUrl: 'https://example.com/qes',
                        referenznummer: '123456',
                        videolegitimationUrl: 'https://example.com/video',
                    },
                    identifikationAntragsteller2: {
                        antragstellername: 'Jane Doe',
                        qesUrl: 'https://example.com/qes',
                        referenznummer: '234567',
                        videolegitimationUrl: 'https://example.com/video',
                    },
                    machbarkeit: {
                        status: EUROPACE_FEASIBILITY_STATUS.MACHBAR,
                    },
                    produktanbieterantragsnummer: '123456',
                    ratenkredit: {
                        produktanbieter: {
                            logo: { svg: 'test-logo' },
                            name: 'Testbank',
                        },
                        produktanbieterId: '123456',
                        produktart: 'Ratenkredit',
                        produktbezeichnung: 'Ratenkredit',
                        schlussrate: 1300.0,
                        vorlaufzinsenProTag: 2.2,
                    },
                    ratenschutz: {
                        praemieBrutto: 120.0,
                        praemieMonatlich: 120.0,
                        praemieNetto: 100.0,
                        produktanbieter: [
                            {
                                logo: { svg: 'test-logo' },
                                name: 'Testbank',
                            },
                        ],
                        versicherteRate: 1000.0,
                        versicherteRisikenAntragsteller1: [EUROPACE_INSURED_RISKS.ARBEITSLOSIGKEIT],
                        versicherteRisikenAntragsteller2: [
                            EUROPACE_INSURED_RISKS.LEBEN,
                            EUROPACE_INSURED_RISKS.ARBEITSUNFAEHIGKEIT,
                        ],
                        versicherungssumme: 10000.0,
                    },
                },
                status: OFFER_ACCEPTANCE_STATUSES.SUCCESS,
            },
            europaceOffer: {
                anpassungen: {},
                antrag: {
                    produktanbieterantragsnummer: '123456',
                },
                gesamtkonditionen: {
                    effektivzins: 0.01,
                    gesamtkreditbetrag: 1000,
                    konditionsspanne: {
                        maximum: {
                            effektivzins: 6.99,
                            gesamtkreditbetrag: 41332.34,
                            rateMonatlich: 3444.36,
                            sollzins: 6.78,
                        },
                        minimum: {
                            effektivzins: 6.99,
                            gesamtkreditbetrag: 41332.34,
                            rateMonatlich: 3444.36,
                            sollzins: 6.78,
                        },
                    },
                    laufzeitInMonaten: 12,
                    nettokreditbetrag: 100,
                    rateMonatlich: 100,
                    sollzins: 0.01,
                },
                id: '22097497-e7b6-402a-a65b-6c02c049d067',
                machbarkeit: {
                    messages: [],
                    status: EUROPACE_FEASIBILITY_STATUS.NICHT_MACHBAR,
                },
                offerId:
                    'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ0ZXN0Ijp0cnVlfQ.cAyyocTRtFnHuVdCAUb5t2Z8fYBSQkotg72u7KTA9tQ',
                ratenkredit: {
                    produktanbieter: {
                        anschrift: {
                            hausnummer: '1',
                            ort: 'Berlin',
                            plz: '12345',
                            strasse: 'Teststraße',
                        },
                        logo: {
                            svg: 'test-logo',
                        },
                        name: 'Testbank',
                    },
                    produktbezeichnung: 'Testprodukt',
                    schlussrate: 0,
                },
                ratenschutz: {
                    praemieMonatlich: 0,
                    versicherteRisikenAntragsteller1: [],
                    versicherteRisikenAntragsteller2: [],
                },
                sofortkredit: false,
                vollstaendigkeit: {
                    messages: [],
                    status: EUROPACE_COMPLETENESS_STATUS.VOLLSTAENDIG,
                },
            },
            id: '22097497-e7b6-402a-a65b-6c02c049d067',
            jobId: 'AB12345678',
            offerId:
                'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ0ZXN0Ijp0cnVlfQ.cAyyocTRtFnHuVdCAUb5t2Z8fYBSQkotg72u7KTA9tQ',
            offerRequestId: '22097497-e7b6-402a-a65b-6c02c049d030',
            partnerId: 'ABC12',
            processId: 'KR3D1T',
            response: {},
        } satisfies EventSchema<LoanRequest_EuropaceOfferAcceptanceResponseSuccessReceived>;
    }

    static exampleCensoredApplicant2Only() {
        const example = LoanRequest_EuropaceOfferAcceptanceResponseSuccessReceived.example();
        if (example.acceptedOffer?.antrag?.identifikationAntragsteller2?.antragstellername) {
            example.acceptedOffer.antrag.identifikationAntragsteller2.antragstellername = DEFAULT_CENSOR_MASK;
        }
        return example;
    }

    static exampleCensored() {
        const example = LoanRequest_EuropaceOfferAcceptanceResponseSuccessReceived.example();
        return {
            ...example,
            acceptedOffer: {
                ...example.acceptedOffer,
                antrag: censorEuropaceAntrag(example.acceptedOffer.antrag as SchemaEuropaceAntrag),
            },
        } satisfies z.infer<
            ReturnType<LoanRequest_EuropaceOfferAcceptanceResponseSuccessReceived['getCensoredSchema']>
        >;
    }
}

export type Schema_LoanRequest_EuropaceOfferAcceptanceResponseSuccessReceived =
    EventSchema<LoanRequest_EuropaceOfferAcceptanceResponseSuccessReceived>;
